import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { theme } from "../theme.js";
import { DEFAULT_IMAGE } from "../utils/constants.js";

const statusConfig = {
  "Sold": {
    label: "Vendu",
    color: theme.colors.success,
    bgColor: "rgba(76, 175, 80, 0.1)",
  },
  "Transferred": {
    label: "Transféré",
    color: theme.colors.primary,
    bgColor: "rgba(33, 150, 243, 0.1)",
  },
  "For Sale": {
    label: "En vente",
    color: theme.colors.warning,
    bgColor: "rgba(255, 193, 7, 0.1)",
  },
  "Waiting transfer": {
    label: "En attente",
    color: theme.colors.warning,
    bgColor: "rgba(255, 193, 7, 0.1)",
  },
};

export default function TransactionTicketCard({ ticket }) {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const status = ticket?.status || "Unknown";
  const config = statusConfig[status] || { label: status, color: theme.colors.textMuted, bgColor: "rgba(0,0,0,0.05)" };

  const handleClick = () => {
    navigate(`/tickets/${ticket?.id_bdd || ticket?.id}`, {
      state: { bibs: [ticket] },
    });
  };

  const raceImage = ticket?.race?.picture || ticket?.race?.image_url || DEFAULT_IMAGE;

  return (
    <div style={styles.card}>
      <div style={{ ...styles.content, flexDirection: isMobile ? "column" : "row" }}>
        <div style={{ ...styles.imageContainer, width: isMobile ? "100%" : "220px", height: isMobile ? "160px" : "220px" }}>
          {raceImage ? (
            <img src={raceImage} alt={ticket?.race?.name} style={styles.image} />
          ) : (
            <div style={styles.defaultImage}>
              <span style={styles.defaultImageIcon}> {DEFAULT_IMAGE}</span>
            </div>
          )}
        </div>
        <div style={styles.info}>
          <div style={styles.header}>
            <div style={styles.titleSection}>
              <h3 style={styles.title}>{ticket?.race?.name || "Course inconnue"}</h3>
              <p style={styles.bibNumber}>Dossard #{ticket?.bib_number || ticket?.number || "—"}</p>
            </div>
            <div style={{ ...styles.badge, backgroundColor: config.bgColor }}>
              <span style={{ ...styles.badgeText, color: config.color }}>
                {config.label}
              </span>
            </div>
          </div>

          <div style={styles.details}>
            <div style={styles.detailRow}>
              <span style={styles.label}>Course:</span>
              <span style={styles.value}>{ticket?.race?.name || "—"}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Date:</span>
              <span style={styles.value}>
                {ticket?.race?.date ? new Date(ticket.race.date).toLocaleDateString("fr-FR") : "—"}
              </span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.label}>Participant:</span>
              <span style={styles.value}>
                {ticket?.participant?.firstname} {ticket?.participant?.name}
              </span>
            </div>
          </div>

          <button style={styles.viewButton} onClick={handleClick}>
            Voir les détails
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: {
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    border: `1px solid ${theme.colors.border}`,
    overflow: "hidden",
    boxShadow: theme.shadows.sm,
    transition: "all 0.2s ease",
    cursor: "pointer",
  },
  content: {
    display: "flex",
    gap: 0,
  },
  imageContainer: {
    width: "220px",
    height: "220px",
    flexShrink: 0,
    overflow: "hidden",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  defaultImage: {
    width: "100%",
    height: "100%",
    background: `linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.primary}10)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRight: `1px solid ${theme.colors.border}`,
  },
  defaultImageIcon: {
    fontSize: "48px",
    opacity: 0.6,
  },
  info: {
    flex: 1,
    padding: theme.spacing.lg,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing.md,
    gap: theme.spacing.md,
  },
  titleSection: {
    flex: 1,
  },
  title: {
    margin: 0,
    fontSize: theme.fonts.size.md,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textDefault,
  },
  bibNumber: {
    margin: `${theme.spacing.xs} 0 0 0`,
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textMuted,
  },
  badge: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.md,
    display: "inline-block",
    whiteSpace: "nowrap",
  },
  badgeText: {
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.semibold,
  },
  details: {
    marginBottom: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    borderBottom: `1px solid ${theme.colors.border}`,
    flex: 1,
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing.sm,
    fontSize: theme.fonts.size.sm,
    gap: theme.spacing.md,
  },
  label: {
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textMuted,
    minWidth: "100px",
    flexShrink: 0,
  },
  value: {
    color: theme.colors.textDefault,
    flex: 1,
  },
  viewButton: {
    width: "100%",
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    background: theme.colors.primary,
    color: theme.colors.textInverted,
    border: "none",
    borderRadius: theme.radius.md,
    cursor: "pointer",
    fontWeight: theme.fonts.weight.semibold,
    fontSize: theme.fonts.size.sm,
    transition: "background 0.2s ease",
  },
};
